import { AbsoluteFill, interpolate, spring, useCurrentFrame, useVideoConfig } from "remotion";
import {
  Caption,
  Callout,
  CodePanel,
  COLORS,
  FONTS,
  Panel,
  PipelineGraph,
  SceneTransition,
  SlideTimeBar,
  type AccentName,
  type PipeNode,
} from "../../coldcompute";
import { SCENE } from "./constants";
import { HOOK, PYRAMID, RLS, CODE, FAILCLOSED, EVERYWHERE, PAYOFF, OUTRO } from "./script";

const center: React.CSSProperties = {
  alignItems: "center",
  justifyContent: "center",
  flexDirection: "column",
};

const Kicker: React.FC<{ text: string; color: string }> = ({ text, color }) => (
  <div
    style={{
      fontFamily: FONTS.mono,
      fontSize: 22,
      letterSpacing: "0.22em",
      textTransform: "uppercase",
      color,
      marginBottom: 22,
    }}
  >
    ▍{text}
  </div>
);

const Big: React.FC<{ children: React.ReactNode; size?: number }> = ({
  children,
  size = 92,
}) => (
  <div
    style={{
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: size,
      lineHeight: 0.99,
      letterSpacing: "-0.02em",
      color: COLORS.text,
      textAlign: "center",
      maxWidth: 1560,
    }}
  >
    {children}
  </div>
);

/* 01 — HOOK: one missed WHERE clause = breach */
export const Scene01Hook: React.FC = () => {
  const frame = useCurrentFrame();
  const leak = interpolate(frame, [44, 78], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.hook.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="multi-tenant · the breach" color={COLORS.red} />
          <Big>
            one missing{" "}
            <span style={{ fontFamily: FONTS.mono, color: COLORS.amber }}>WHERE</span>{" "}
            clause
            <br />
            leaks{" "}
            <span style={{ color: COLORS.red, opacity: 0.35 + leak * 0.65 }}>
              another tenant's data
            </span>
          </Big>
          <div
            style={{
              fontFamily: FONTS.mono,
              fontSize: 26,
              color: COLORS.dim,
              marginTop: 34,
            }}
          >
            tenant A → tenant B · product dead
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={HOOK} accent="red" />
    </AbsoluteFill>
  );
};

/* 02 — PYRAMID: 5-layer defense-in-depth */
const LAYERS: { n: number; label: string; sub: string; accent: AccentName }[] = [
  { n: 5, label: "Audit & Monitoring", sub: "audit_logs · rate limits", accent: "violet" },
  { n: 4, label: "Data Protection", sub: "AES-256-GCM · PII · injection", accent: "magenta" },
  { n: 3, label: "Authorization", sub: "RLS · RBAC · source ACL", accent: "cyan" },
  { n: 2, label: "Authentication", sub: "JWT · CSRF · lockout", accent: "green" },
  { n: 1, label: "Network", sub: "HTTPS · CORS · CSP", accent: "lime" },
];
export const Scene02Pyramid: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.pyramid.duration} />
      <AbsoluteFill style={{ ...center, padding: "0 80px" }}>
        <Kicker text="defense-in-depth · 5 layers" color={COLORS.cyan} />
        <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
          {LAYERS.map((L, i) => {
            const appear = spring({
              frame: frame - (10 + i * 14),
              fps,
              config: { damping: 200, mass: 0.6, stiffness: 180 },
              durationInFrames: 12,
            });
            const isRls = L.n === 3;
            const width = 560 + i * 130;
            const c = COLORS[L.accent];
            return (
              <div
                key={L.n}
                style={{
                  width,
                  opacity: appear,
                  transform: `translateY(${(1 - appear) * 16}px)`,
                  background: COLORS.panel,
                  border: `${isRls ? 2 : 1}px solid ${isRls ? c : `${c}66`}`,
                  borderRadius: 10,
                  boxShadow: isRls ? `0 0 36px -12px ${c}` : `0 18px 50px -34px #000`,
                  padding: "13px 22px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                  <span
                    style={{
                      fontFamily: FONTS.mono,
                      fontSize: 18,
                      color: c,
                      width: 28,
                    }}
                  >
                    L{L.n}
                  </span>
                  <span
                    style={{
                      fontFamily: FONTS.display,
                      fontWeight: 600,
                      fontSize: 27,
                      color: COLORS.text,
                    }}
                  >
                    {L.label}
                  </span>
                </div>
                <span style={{ fontFamily: FONTS.mono, fontSize: 16, color: COLORS.dim }}>
                  {L.sub}
                </span>
              </div>
            );
          })}
        </div>
      </AbsoluteFill>
      <Caption lines={PYRAMID} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 03 — RLS: Layer 3 is the big one */
export const Scene03Rls: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.rls.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Kicker text="layer 3 · the load-bearing one" color={COLORS.cyan} />
        <Big size={76}>
          Row-Level Security
          <br />
          <span style={{ color: COLORS.cyan }}>enforced by Postgres</span>
        </Big>
        <div style={{ display: "flex", gap: 26, marginTop: 40 }}>
          <Panel accent="cyan" title="the row" width={420}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>id <span style={{ color: COLORS.dim }}>uuid</span></div>
              <div style={{ color: COLORS.cyan }}>tenant_id <span style={{ color: COLORS.dim }}>uuid</span></div>
              <div>data <span style={{ color: COLORS.dim }}>…</span></div>
            </div>
          </Panel>
          <div style={{ fontFamily: FONTS.display, fontSize: 50, color: COLORS.dim, alignSelf: "center" }}>
            ⇄
          </div>
          <Panel accent="green" title="the JWT claim" width={420}>
            <div style={{ fontFamily: FONTS.mono, fontSize: 22, lineHeight: 1.9, color: COLORS.text }}>
              <div>sub <span style={{ color: COLORS.dim }}>user</span></div>
              <div style={{ color: COLORS.green }}>tenant_id <span style={{ color: COLORS.dim }}>uuid</span></div>
              <div>role <span style={{ color: COLORS.dim }}>admin?</span></div>
            </div>
          </Panel>
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={RLS} accent="cyan" />
  </AbsoluteFill>
);

/* 04 — CODE: the real mig 194 RLS policy */
const SQL = `-- 194_llm_call_costs_rls_policy.sql
-- Pattern shared by migrations 188–194: JWT-claim tenant isolation.

DROP POLICY IF EXISTS llm_call_costs_tenant_isolation
  ON public.llm_call_costs;

CREATE POLICY llm_call_costs_tenant_isolation
  ON public.llm_call_costs
  USING      (tenant_id::text = (auth.jwt() ->> 'tenant_id'))
  WITH CHECK (tenant_id::text = (auth.jwt() ->> 'tenant_id'));`;
export const Scene04Code: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.code.duration} />
    <AbsoluteFill style={{ alignItems: "center", justifyContent: "center", paddingBottom: 36 }}>
      <CodePanel
        code={SQL}
        language="sql"
        filename="194_llm_call_costs_rls_policy.sql"
        highlightLines={[8, 9]}
        revealFrames={300}
        fontSize={25}
        width={1300}
      />
    </AbsoluteFill>
    <Caption lines={CODE} accent="cyan" />
  </AbsoluteFill>
);

/* 05 — FAIL-CLOSED: RLS ON + no policy = deny-all */
export const Scene05FailClosed: React.FC = () => {
  const frame = useCurrentFrame();
  const zero = interpolate(frame, [60, 95], [0, 1], {
    extrapolateLeft: "clamp",
    extrapolateRight: "clamp",
  });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.failclosed.duration} />
      <SceneTransition from="up">
        <AbsoluteFill style={center}>
          <Kicker text="the safe state is the default" color={COLORS.green} />
          <div style={{ display: "flex", gap: 28, alignItems: "stretch" }}>
            <Panel accent="green" title="ENABLE ROW LEVEL SECURITY" width={520}>
              <div style={{ fontFamily: FONTS.body, fontSize: 24, lineHeight: 1.55, color: COLORS.text }}>
                RLS on, no policy yet →
                <br />
                Postgres denies <b>everyone</b> by default.
              </div>
            </Panel>
            <Panel accent="red" title="no JWT · no tenant claim" width={420}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: 130 }}>
                <div
                  style={{
                    fontFamily: FONTS.display,
                    fontWeight: 700,
                    fontSize: 78,
                    color: COLORS.red,
                    opacity: 0.4 + zero * 0.6,
                  }}
                >
                  0 rows
                </div>
                <div style={{ fontFamily: FONTS.mono, fontSize: 18, color: COLORS.dim }}>
                  fail closed
                </div>
              </div>
            </Panel>
          </div>
        </AbsoluteFill>
      </SceneTransition>
      <Caption lines={FAILCLOSED} accent="green" />
    </AbsoluteFill>
  );
};

/* 06 — EVERYWHERE: tenant_id woven through everything */
const PIPE: PipeNode[] = [
  { label: "request", value: "JWT", sub: "tenant_id", accent: "cyan" },
  { label: "db", value: "RLS", sub: "row filter", accent: "green" },
  { label: "cache", value: "key", sub: "tenant-scoped", accent: "lime" },
  { label: "mcp", value: "API key", sub: "→ tenant", accent: "amber" },
  { label: "audit", value: "logs", sub: "per tenant", accent: "magenta" },
];
export const Scene06Everywhere: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.everywhere.duration} />
    <AbsoluteFill style={{ ...center, padding: "0 70px" }}>
      <Kicker text="tenant_id in 8 places · not just the DB" color={COLORS.lime} />
      <div style={{ width: 1700 }}>
        <PipelineGraph nodes={PIPE} startFrame={8} stepFrames={20} nodeWidth={200} />
      </div>
      <div style={{ fontFamily: FONTS.body, fontSize: 26, color: COLORS.dim, marginTop: 44 }}>
        cache · MCP keys · audit · rate limits · knowledge graph · source ACL
      </div>
    </AbsoluteFill>
    <Caption lines={EVERYWHERE} accent="lime" />
  </AbsoluteFill>
);

/* 07 — PAYOFF: the engine catches the missed WHERE */
export const Scene07Payoff: React.FC = () => {
  const frame = useCurrentFrame();
  const c1 = interpolate(frame, [120, 150], [0, 1], { extrapolateLeft: "clamp", extrapolateRight: "clamp" });
  return (
    <AbsoluteFill style={center}>
      <SlideTimeBar sceneDurationFrames={SCENE.payoff.duration} />
      <AbsoluteFill style={center}>
        <Kicker text="even with the bug" color={COLORS.cyan} />
        <div
          style={{
            fontFamily: FONTS.mono,
            fontSize: 40,
            color: COLORS.amber,
            background: COLORS.panel,
            border: `1px solid ${COLORS.line}`,
            borderRadius: 10,
            padding: "16px 28px",
          }}
        >
          SELECT * FROM chat_sessions;{" "}
          <span style={{ color: COLORS.red }}>-- no WHERE</span>
        </div>
        <div style={{ fontFamily: FONTS.display, fontSize: 46, color: COLORS.dim, margin: "26px 0" }}>
          ↓
        </div>
        <Big size={66}>
          the engine returns{" "}
          <span style={{ color: COLORS.green }}>only your tenant's rows</span>
        </Big>
        <div style={{ marginTop: 38, opacity: c1 }}>
          <Callout label="not a JS filter · a Postgres constraint" accent="green" width={620}>
            Isolation lives at the database row — where a code bug can't bypass it.
          </Callout>
        </div>
      </AbsoluteFill>
      <Caption lines={PAYOFF} accent="cyan" />
    </AbsoluteFill>
  );
};

/* 08 — OUTRO */
export const Scene08Outro: React.FC = () => (
  <AbsoluteFill style={center}>
    <SlideTimeBar sceneDurationFrames={SCENE.outro.duration} />
    <SceneTransition from="up">
      <AbsoluteFill style={center}>
        <Big size={70}>
          Enterprise isolation, solo:
          <br />
          <span style={{ color: COLORS.cyan }}>put the guarantee at the row.</span>
        </Big>
        <div style={{ fontFamily: FONTS.mono, fontSize: 24, letterSpacing: "0.24em", color: COLORS.dim, marginTop: 44 }}>
          EAKC · multi-tenant security
        </div>
      </AbsoluteFill>
    </SceneTransition>
    <Caption lines={OUTRO} accent="cyan" />
  </AbsoluteFill>
);

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
